import React, { useContext, useEffect, useState } from "react";

import {
  Alert,
  FormGroup,
  Icon,
  NotificationManager,
  Select,
  Spinner,
} from "design-react-kit";
import {
  createQueryString,
  sendApiRequest,
} from "../services/Base/base-service";
import UserContext from "../store/user-context";
import { useSignIn, useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";

const DragNdrop = ({ width, height }) => {
  const [clientCodes, setClientCodes] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [isUploadStarted, setIsUploadStarted] = useState(false);
  const [uploadedFilesCount, setUploadedFilesCount] = useState(0);
  const [failedFilesCount, setFailedFilesCount] = useState(0);

  const userContext = useContext(UserContext);

  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();

  useEffect(() => {
    if (userContext.selectedClientFiscalCode !== null) {
      getAllClientCodesHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext.selectedClientFiscalCode]);

  const getAllClientCodesHandler = () => {
    setError(null);
    sendApiRequest(
      "Client/GetAllClientsForUpload",
      "GET",
      true,
      { fiscalCode: userContext.selectedClientFiscalCode, flowId: null },
      signIn,
      signOut,
      navigate,
      createQueryString,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
        } else {
          setError(null);
          let clientCodes = [];
          response.data.map((item) =>
            clientCodes.push({
              value: item.clientId,
              label: item.codeName,
            })
          );
          setClientCodes(clientCodes);
        }
      }
    });
  };

  const handleClientCodeChange = (selectedOption) =>
    setSelectedClient(selectedOption);

  const handleFileChange = (event) => {
    event.preventDefault();
    if (!isUploadStarted) {
      setError(null);
      const selectedFiles = event.target.files;
      validateFiles(selectedFiles);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (!isUploadStarted) {
      setError(null);
      const droppedFiles = event.dataTransfer.files;
      validateFiles(droppedFiles);
    }
  };

  const validateFiles = (filesList) => {
    if (filesList && filesList.length > 0) {
      const newFiles = Array.from(filesList);

      const totalSize = newFiles.reduce((acc, file) => acc + file.size, 0);

      if (totalSize > 50000000) {
        setError([
          "Il file non può essere caricato perchè la somma finale supererebbe i 50 Mb.",
        ]);
      } else {
        let filteredArr = newFiles.filter((f) => f.type === "application/pdf");
        let discardedArr = newFiles.filter((f) => f.type !== "application/pdf");
        let discardedNames = [];
        discardedArr.forEach((file, index) => {
          discardedNames[index] = file.name;
        });

        if (discardedNames.length > 0) {
          setError([
            `I seguenti files non sono stati accettati perchè non riconosciuti come PDF: `,
            ...discardedNames,
          ]);
        }

        if (files !== null && files.length > 0) {
          const commonFiles = filteredArr.filter((file1) =>
            files.some(
              (file2) => file2.name.toLowerCase() === file1.name.toLowerCase()
            )
          );
          let commonNames = [];
          commonFiles.forEach((file, index) => {
            commonNames[index] = file.name;
          });

          filteredArr.forEach((f) => {
            if (!commonNames.includes(f.name)) {
              setFiles((prevFiles) => [...prevFiles, f]);
            } else {
              setError((prevState) => {
                if (prevState === null) {
                  return [
                    `${f.name} è già presente nell'elenco dei files selezionati.`,
                  ];
                } else {
                  return [
                    ...prevState,
                    `${f.name} è già presente nell'elenco dei files selezionati.`,
                  ];
                }
              });
            }
          });
        } else {
          setFiles((prevFiles) => [...prevFiles, ...filteredArr]);
        }
      }
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const uploadFileHandler = () => {
    setError(null);
    setIsUploadStarted(true);

    const chunks = files.reduce((acc, item, index) => {
      if (index % 10 === 0) acc.push([]);
      acc[acc.length - 1].push(item);
      return acc;
    }, []);

    chunks.forEach(async (chunk) => {
      const filesList = [];

      chunk.forEach((f) => {
        filesList.push({ fileName: f.name, fileContentType: f.type });
        setIsLoading((prevState) => ({ ...prevState, [f.name]: true }));
      });

      let response = await getPresignedUrls(filesList);

      if (
        response.filesWithWrongNames !== undefined &&
        response.filesWithWrongNames !== null &&
        response.filesWithWrongNames.length > 0
      ) {
        response.filesWithWrongNames.forEach((file) => {
          uploadCompletedHandler(file, response);
        });
      }

      if (
        response.preSignedUrls !== undefined &&
        response.preSignedUrls !== null &&
        response.preSignedUrls.length > 0
      ) {
        chunk.forEach(async (file) => {
          let url = response.preSignedUrls.filter(
            (url) => url.fileName === file.name
          )[0];
          await uploadFileToS3(url.preSignedUrl, file, response);
        });
      }
    });
  };

  const getPresignedUrls = async (files) => {
    return sendApiRequest(
      `Document/GetFilePreSignedUrls`,
      "POST",
      true,
      { clientId: selectedClient.value, files },
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
        } else {
          return response.data;
        }
      }
    });
  };

  const isValidAuthorizedUrl = (url) => {
    try {
      const parsedUrl = new URL(url);

      const allowedHostPattern = /^.*\.s3\.eu-central-1\.amazonaws\.com$/;
      if (!allowedHostPattern.test(parsedUrl.host)) {
        return false;
      }

      const requiredParams = [
        "X-Amz-Expires",
        "X-Amz-Algorithm",
        "X-Amz-Credential",
        "X-Amz-Date",
        "X-Amz-SignedHeaders",
        "X-Amz-Signature",
      ];
      for (const param of requiredParams) {
        if (!parsedUrl.searchParams.has(param)) {
          return false;
        }
      }

      const fileName = decodeURIComponent(parsedUrl.pathname).slice(1);

      const allowedFilePattern = /^[\w_#-\s]+\.pdf$/i;
      if (!allowedFilePattern.test(fileName)) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  };

  const uploadFileToS3 = async (url, file, resp) => {
    if (isValidAuthorizedUrl(url)) {
      try {
        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        let errors = null;
        if (!response.ok) {
          errors = (await response.json()).errors;
          setError(errors);
        }

        uploadCompletedHandler(file.name, resp);
        logS3BucketUploadResult(file, errors);
      } catch (error) {
        if (error.stack.includes("TypeError: ")) {
          setError([
            "Il caricamento non è andato a buon fine per motivi tecnici, contatta il supporto tecnico di EasyNotification",
          ]);
        } else {
          setError([error]);
        }
        setIsLoading((prevState) => ({ ...prevState, [file.name]: false }));
        setUploadResponse((prevState) => {
          if (prevState === null) {
            return { [file.name]: false };
          } else {
            return { ...prevState, [file.name]: false };
          }
        });
        setFailedFilesCount((prevState) => (prevState += 1));
      }
    } else {
      setError([`L'URL prefirmato per il file ${file.name} non è consentito`]);
      setIsLoading((prevState) => ({ ...prevState, [file.name]: false }));
      setUploadResponse((prevState) => {
        if (prevState === null) {
          return { [file.name]: false };
        } else {
          return { ...prevState, [file.name]: false };
        }
      });
      setFailedFilesCount((prevState) => (prevState += 1));
    }
  };

  let isUploadButtonDisabled =
    selectedClient === null ||
    selectedClient === undefined ||
    selectedClient.length === 0 ||
    files === null ||
    files.length === 0;

  const uploadCompletedHandler = (filename, response) => {
    if (response.errors !== null && response.errors !== undefined) {
      setError(response.errors);
      setIsLoading((prevState) => ({ ...prevState, [filename]: false }));
      setFailedFilesCount((prevState) => (prevState += 1));
    } else {
      if (
        response.preSignedUrls !== undefined &&
        response.preSignedUrls !== null &&
        response.preSignedUrls.length > 0 &&
        response.preSignedUrls.some((element) => element.fileName === filename)
      ) {
        setUploadedFilesCount((prevState) => (prevState += 1));
        setUploadResponse((prevState) => {
          if (prevState === null) {
            return { [filename]: true };
          } else {
            return { ...prevState, [filename]: true };
          }
        });
      }

      if (
        response.filesWithWrongNames !== undefined &&
        response.filesWithWrongNames !== null &&
        response.filesWithWrongNames.length > 0 &&
        response.filesWithWrongNames.includes(filename)
      ) {
        setFailedFilesCount((prevState) => (prevState += 1));
        setUploadResponse((prevState) => {
          if (prevState === null) {
            return { [filename]: false };
          } else {
            return { ...prevState, [filename]: false };
          }
        });
        setError([
          `I seguenti files non sono stati accettati perchè non identificano nessuno tra i tipi di documenti attesi (${response.documentNamePatterns
            .map((p) => `*${p}*`)
            .join(", ")})`,
          ...response.filesWithWrongNames,
        ]);
      }

      setIsLoading((prevState) => ({ ...prevState, [filename]: false }));
    }
  };

  const logS3BucketUploadResult = (file, error) => {
    sendApiRequest(
      `Log/LogS3BucketUploadResult`,
      "POST",
      true,
      {
        clientId: selectedClient.value,
        fileName: file.name,
        fileSize: file.size,
        fileContentType: file.type,
        error: error,
      },
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
        }
      }
    });
  };

  const startNewUpload = () => {
    setError(null);
    setFiles([]);
    setIsUploadStarted(false);
    setSelectedClient(null);
    setUploadResponse(null);
    setIsLoading(null);
    setFailedFilesCount(0);
    setUploadedFilesCount(0);
  };

  const showFileSize = (bytes) => {
    if (bytes > 1000000) {
      return `${(bytes / 1000000).toFixed(2)}MB`;
    } else {
      return `${(bytes / 1000).toFixed(2)}KB`;
    }
  };

  return (
    <section className="drag-drop">
      {(clientCodes === null || clientCodes.length === 0) && (
        <Alert color="info" className="mx-auto w-75">
          Nessuna tipologia di servizio abilitata all'utilizzo del modulo AI.
        </Alert>
      )}
      {clientCodes !== null && clientCodes.length > 0 && (
        <div
          style={{
            minWidth: width,
            minHeight: height,
            marginTop: "2rem",
            marginBottom: "2rem",
          }}
        >
          {error !== null && error.length !== undefined && (
            <Alert color="danger" className="mx-auto w-100 mb-5">
              {error.map((item) => (
                <p key={item}>{item}</p>
              ))}
            </Alert>
          )}
          <FormGroup className="col-sm-12 m-0 mb-5 position-relative mt-3">
            <div className="bootstrap-select-wrapper col-12">
              <label htmlFor="userClients">Tipologia di servizio</label>
              <Select
                id="userClients"
                isMulti={false}
                onChange={handleClientCodeChange}
                options={clientCodes}
                value={selectedClient}
                placeholder="-Seleziona la tipologia di servizio-"
                aria-label="Seleziona"
              />
            </div>
          </FormGroup>
          <div
            className={`document-uploader ${
              files.length > 0 ? "upload-box activeUpload" : "upload-box"
            }`}
            onDrop={handleDrop}
            onDragOver={(event) => event.preventDefault()}
          >
            <>
              {!isUploadStarted && (
                <div
                  className={`upload-info ${
                    files.length > 0 ? "upload-info-activeUpload" : ""
                  }`}
                >
                  <Icon icon="it-upload" fill="#06c" />
                  <div>
                    <p>Trascina e rilascia i tuoi file PDF qui.</p>
                    <p>
                      La somma di tutti i files PDF selezionati non può superare
                      50 Mb.
                    </p>
                  </div>
                </div>
              )}
              {isUploadStarted && (
                <div
                  className={`upload-info ${
                    files.length > 0 ? "upload-info-activeUpload" : ""
                  }`}
                >
                  <Icon icon="it-upload" fill="#06c" />
                  <div>
                    <p>Esito caricamento files.</p>
                    <p>
                      Se devi trasmettere altri file premi il pulsante ' Nuovo
                      caricamento' .
                    </p>
                  </div>
                </div>
              )}
            </>
            {files.length === 0 && (
              <>
                <input
                  type="file"
                  hidden
                  id="browse"
                  onChange={handleFileChange}
                  accept=".pdf"
                  multiple
                />
                <label htmlFor="browse" className="browse-btn mt-3">
                  Seleziona files
                </label>
              </>
            )}
            {files.length > 0 && (
              <div className="file-list">
                <div className="file-list__container">
                  {files.map((file, index) => (
                    <div className="file-item" key={index}>
                      <div className="file-info">
                        <p>
                          {file.name} ({showFileSize(file.size)})
                        </p>
                      </div>
                      {(isLoading === null || !isLoading[file.name]) &&
                        uploadResponse === null && (
                          <div
                            className={`file-actions-${file.name}`}
                            title="Rimuovi dall'elenco"
                          >
                            <Icon
                              icon="it-delete"
                              fill="#000"
                              onClick={() => handleRemoveFile(index)}
                            />
                          </div>
                        )}
                      {(isLoading === null || !isLoading[file.name]) &&
                        uploadResponse !== null &&
                        uploadResponse[file.name] && (
                          <div
                            className={`success-${file.name}`}
                            title="Caricato"
                          >
                            <Icon icon="it-check" fill="#2cb61d" />
                          </div>
                        )}
                      {(isLoading === null || !isLoading[file.name]) &&
                        uploadResponse !== null &&
                        !uploadResponse[file.name] && (
                          <div className={`fail-${file.name}`} title="Fallito">
                            <Icon icon="it-close" fill="#d70049" />
                          </div>
                        )}
                      {isLoading !== null && isLoading[file.name] && (
                        <p
                          className="file-info-spinner"
                          style={{
                            height: "10px",
                            position: "relative",
                            marginBottom: "2rem",
                            marginRight: "3rem",
                          }}
                        >
                          <Spinner double active />
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {files.length > 0 && !isUploadStarted && (
            <>
              <div className="success-file mb-3">
                <p>
                  <Icon icon="it-check" fill="#0073e6" /> {files.length} file(s)
                  pronti per essere caricati
                </p>
              </div>
              <div className="d-flex align-items-center justify-content-around">
                <input
                  type="file"
                  hidden
                  id="browse"
                  onChange={handleFileChange}
                  accept=".pdf"
                  multiple
                />
                <label htmlFor="browse" className="browse-btn">
                  Seleziona files
                </label>

                <button
                  disabled={isUploadButtonDisabled}
                  className={
                    isUploadButtonDisabled
                      ? "browse-btn disabled"
                      : "browse-btn"
                  }
                  onClick={(e) =>
                    !isUploadButtonDisabled ? uploadFileHandler() : null
                  }
                >
                  Carica files
                </button>
              </div>
            </>
          )}
          {files.length > 0 && isUploadStarted && (
            <div className="d-flex flex-column align-items-center justify-content-center">
              <div className="d-flex align-items-center justify-content-between w-100 mb-3">
                <div className="success-file-uploaded">
                  <p>
                    <Icon icon="it-check" fill="#2cb61d" /> {uploadedFilesCount}{" "}
                    file(s) caricati con successo
                  </p>
                </div>
                <div className="failed-file-uploaded">
                  <p>
                    <Icon icon="it-close" fill="#d44" /> {failedFilesCount}{" "}
                    file(s) non trasmessi
                  </p>
                </div>
              </div>
              <button className="browse-btn" onClick={(e) => startNewUpload()}>
                Nuovo caricamento
              </button>
            </div>
          )}
        </div>
      )}
      {/* {error !== null && error.length !== undefined && (
        <Alert color="danger" className="mx-auto w-75">
          {error.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </Alert>
      )} */}
      <div>
        <NotificationManager
          containerId="upload-files-notification"
          fix="right"
          closeOnClick={true}
          state="error"
          duration={30000}
        />
      </div>
    </section>
  );
};

export default DragNdrop;
